export const ALLOW_AUTOFILL_ATTR = "data-allow-autofill"

type TextField = HTMLInputElement | HTMLTextAreaElement

// Nothing autofills these, so tagging them is only DOM churn.
const NON_TEXT_INPUT_TYPES = new Set([
	"checkbox",
	"radio",
	"file",
	"hidden",
	"button",
	"submit",
	"reset",
	"image",
	"range",
	"color",
])

const isTextField = (el: Element): el is TextField =>
	el.tagName === "TEXTAREA" ||
	(el.tagName === "INPUT" &&
		!NON_TEXT_INPUT_TYPES.has((el as HTMLInputElement).type))

// Rewriting an unchanged value still queues a mutation record for every other observer.
function setAttr(el: Element, name: string, value: string) {
	if (el.getAttribute(name) !== value) el.setAttribute(name, value)
}

export function suppressAutofill(el: TextField) {
	if (el.hasAttribute(ALLOW_AUTOFILL_ATTR)) return
	// ProseMirror reads any attribute change in its DOM as an edit and redraws the
	// node, which mounts a fresh field to tag: an endless loop.
	if (el.closest(".ProseMirror")) return
	setAttr(el, "autocomplete", "off")
	setAttr(el, "data-1p-ignore", "true")
	setAttr(el, "data-lpignore", "true")
	setAttr(el, "data-protonpass-ignore", "true")
	setAttr(el, "data-bwignore", "true")
	setAttr(el, "data-form-type", "other")
	// Safari's Passwords AutoFill ignores autocomplete="off" but skips fields named like a search box.
	// Named fields keep their name: react-hook-form reads it on every change.
	if (!el.getAttribute("name")) el.setAttribute("name", "search")
}

function suppressWithin(node: Node) {
	if (!(node instanceof Element)) return
	if (isTextField(node)) suppressAutofill(node)
	for (const field of node.querySelectorAll<TextField>("input, textarea")) {
		if (isTextField(field)) suppressAutofill(field)
	}
}

/**
 * Turns off browser and password-manager autofill on every input and textarea
 * under `root`, now and as they mount. Opt a field back in with `data-allow-autofill`.
 */
export function installAutofillGuard(root: HTMLElement = document.body) {
	suppressWithin(root)
	const observer = new MutationObserver((mutations) => {
		for (const mutation of mutations) {
			if (mutation.type === "attributes") {
				if (isTextField(mutation.target as Element)) {
					suppressAutofill(mutation.target as TextField)
				}
				continue
			}
			mutation.addedNodes.forEach(suppressWithin)
		}
	})
	observer.observe(root, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ["autocomplete", "name", ALLOW_AUTOFILL_ATTR],
	})
	// Catches a field that gains focus before the observer's microtask runs.
	const onFocus = (e: FocusEvent) => {
		if (e.target instanceof Element && isTextField(e.target)) {
			suppressAutofill(e.target)
		}
	}
	root.addEventListener("focus", onFocus, true)
	return () => {
		observer.disconnect()
		root.removeEventListener("focus", onFocus, true)
	}
}
